import { components } from '@api/club/generated';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { Badge } from '@ui/components/Badge';
import { Button } from '@ui/components/Button';
import { Card, CardProps } from '@ui/components/Card';
import { DateView } from '@ui/components/DateView';
import { TimeView } from '@ui/components/DateView/DateView';
import { CertificateSealIcon } from '@ui/icons/CertificateSealIcon';

import {
  ClockControlSettings,
  ClockControlType,
  ParticipantStatus,
  TournamentEntrance,
  TournamentHighlight,
  TournamentMode,
  TournamentStatus,
  TournamentSystem,
} from '@features/shared/api/typings';
import { EventEntrance } from '@features/shared/ui/EventEntrance';
import { TournamentSystemView } from '@features/shared/ui/TournamentSystemView';
import { useTournamentParticipantState } from '@features/tournament/hooks/useTournamentParticipantState';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';

import styles from './ClubEventCard.module.css';

const PARTICIPANT_STATUS_MAP: { [status in ParticipantStatus]: string } = {
  Invited: 'Приглашен',
  Participating: 'Я участвую',
  Exited: 'Вышел',
  Banned: 'Исключен',
  Deleted: 'Удален',
  JoinRequest: 'Заявка',
  Offline: 'Удален (оффлайн)',
  Removed: 'Удален',
};

export type Props = {
  tournament: {
    tournamentId: string;
    title: string;
    status: TournamentStatus;
    highlight: TournamentHighlight;
    entrance: TournamentEntrance;
    participantPreModerationEnabled: boolean;
    mode: TournamentMode;
    system: TournamentSystem;
    clockControl?: ClockControlType;
    clockControlSettings?: ClockControlSettings;
    startTimestampMs?: number | null;
    participationStatus?: ParticipantStatus;
    participantsCount: number;
    participantsCountMax?: number | null;
    participationResultType?: components['schemas']['ParticipantResultType'];
    participationResultPlace?: string | null;
    entranceTicketSets?: string[][] | null;
    joinEligibility: components['schemas']['ParticipantJoinEligibility'];
  };
  customEntranceText?: string;
  getTournamentHref?: (tournamentId: string) => string;
  theme?: CardProps['theme'];
  isManager?: boolean;
  wide?: boolean;
  showSeal?: boolean;
};

export function ClubEventCard({
  tournament,
  theme,
  isManager,
  wide,
  showSeal,
  customEntranceText,
  getTournamentHref,
}: Props) {
  const { isLive } = useTournamentStates(tournament);
  const { canJoin, canJoinWithTicket } = useTournamentParticipantState(tournament);

  const { participationStatus, participationResultType, status, participationResultPlace } =
    tournament;

  const badgeText = (() => {
    if (participationStatus) {
      if (participationStatus === 'Participating' && status === 'Finished') {
        switch (participationResultType) {
          case 'Win':
            return 'Победа';
          case 'Loss':
            return 'Проигрыш';
          case 'Draw':
            return 'Ничья';
          case 'KnockedOut':
            return 'Вылет';
          case 'Place':
            return `${participationResultPlace} место`;
        }
      }

      return PARTICIPANT_STATUS_MAP[participationStatus];
    } else if (canJoin) {
      return 'Принять участие';
    } else if ((theme === 'grand-prix' || theme === 'premium-rcf-red') && canJoinWithTicket) {
      return 'Участвовать по подписке';
    } else if (status !== 'Finished') {
      return 'Смотреть онлайн';
    } else if (status === 'Finished') {
      return 'Смотреть результаты';
    }
  })();

  const isParticipating = participationStatus === 'Participating' && status !== 'Finished';

  const isHighlighted = tournament.highlight === 'Pinned' || tournament.highlight === 'Promoted';

  return (
    <Link to={getTournamentHref?.(tournament.tournamentId)} from="/">
      <Card
        flat
        active={isHighlighted}
        disabled={status === 'Draft'}
        className={cx(styles.event, {
          [styles.live]: isLive,
          [styles.wide]: wide,
          [styles.themeGrandPrix]: theme === 'grand-prix',
          [styles.themePremiumRcfRed]: theme === 'premium-rcf-red',
          [styles.themePremiumRcfGreen]: theme === 'premium-rcf-green',
          [styles.participating]: isParticipating,
          [styles.withSeal]: showSeal,
        })}
        theme={theme}
      >
        <div className={styles.title}>
          <span className={styles.titleText}>{tournament.title}</span>
          {showSeal && <CertificateSealIcon className={styles.seal} />}
        </div>

        <div className={styles.entrance}>
          <EventEntrance
            tournament={tournament}
            isManager={isManager}
            customEntranceText={customEntranceText}
          />
        </div>
        <div className={styles.system}>
          <TournamentSystemView showParticipants tournament={tournament} />
        </div>

        <div className={styles.date}>
          {tournament.startTimestampMs ? (
            <>
              <DateView year="hide" date={tournament.startTimestampMs} />
              {isLive ? (
                <Badge size="s" className={styles.liveBadge}>
                  Идет
                </Badge>
              ) : (
                <TimeView date={tournament.startTimestampMs} />
              )}
            </>
          ) : (
            <>
              <span>—</span>
              <span>—</span>
            </>
          )}
        </div>

        {badgeText && (
          <div>
            <Button className={styles.badge} variant="slim">
              {badgeText}
              {isParticipating && <CheckMarkIcon />}
            </Button>
          </div>
        )}
      </Card>
    </Link>
  );
}

function CheckMarkIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 9" width="10">
      <path stroke="currentColor" strokeWidth="1.4" d="m.514 3.677 3 3.25 5-6.5" />
    </svg>
  );
}
